import { useState } from 'react';
import { 
  Mail, Phone, FileText, Send, Brain, Zap, Calendar, 
  Users, AlertTriangle, CheckCircle, Loader2, X, 
  TrendingUp, Clock, Target, RefreshCw, MessageSquare,
  Briefcase, Building, Euro, Star, ThumbsUp
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// ============================================
// CONTACT AI ACTIONS
// ============================================

export function ContactAIActions({ contact, onActionComplete }) {
  const [loading, setLoading] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showDevisModal, setShowDevisModal] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  
  // Email state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [generatingEmail, setGeneratingEmail] = useState(false);

  const handleAnalyzeContact = async () => {
    setLoading('analyze');
    try {
      const response = await api.post(`/ai/analyze-contact`, { contact_id: contact.contact_id });
      setAnalysis(response.data);
      setShowAnalysisModal(true);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setLoading(null);
    }
  };

  const handleGenerateEmail = async (type = 'first_contact') => {
    setGeneratingEmail(true);
    try {
      const response = await api.post('/ai/generate-email', {
        contact_id: contact.contact_id,
        email_type: type,
        context: {
          name: contact.nom_prenom,
          email: contact.email,
          status: contact.statut_pipeline
        }
      });
      setEmailSubject(response.data.subject || '');
      setEmailBody(response.data.body || '');
      toast.success('Email généré par l\'IA');
    } catch (error) {
      toast.error('Erreur de génération');
    } finally {
      setGeneratingEmail(false);
    }
  };

  const handleSendEmail = async () => {
    if (!contact.email || !emailSubject || !emailBody) {
      toast.error('Email, sujet et contenu requis');
      return;
    }
    
    setLoading('send');
    try {
      await api.post('/emails/send', {
        to: contact.email,
        subject: emailSubject,
        body: emailBody,
        contact_id: contact.contact_id
      });
      toast.success('Email envoyé !');
      setShowEmailModal(false);
      setEmailSubject('');
      setEmailBody('');
      onActionComplete?.();
    } catch (error) {
      toast.error('Erreur d\'envoi');
    } finally {
      setLoading(null);
    }
  };

  const handleCall = () => {
    if (contact.telephone) {
      window.open(`tel:${contact.telephone}`, '_self');
      toast.info(`Appel vers ${contact.telephone}`);
    } else {
      toast.error('Pas de numéro de téléphone');
    }
  };

  return (
    <>
      {/* Action Buttons Row */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => { setShowEmailModal(true); handleGenerateEmail('first_contact'); }}
          disabled={!contact.email || loading === 'email'}
          className="text-blue-600 border-blue-200 hover:bg-blue-50"
          data-testid={`contact-action-email-${contact.contact_id}`}
        >
          {loading === 'email' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          <span className="ml-1 hidden sm:inline">Email IA</span>
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={handleCall}
          disabled={!contact.telephone}
          className="text-green-600 border-green-200 hover:bg-green-50"
          data-testid={`contact-action-call-${contact.contact_id}`}
        >
          <Phone className="w-3 h-3" />
          <span className="ml-1 hidden sm:inline">Appeler</span>
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowDevisModal(true)}
          className="text-purple-600 border-purple-200 hover:bg-purple-50"
          data-testid={`contact-action-devis-${contact.contact_id}`}
        >
          <FileText className="w-3 h-3" />
          <span className="ml-1 hidden sm:inline">Devis</span>
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={handleAnalyzeContact}
          disabled={loading === 'analyze'}
          className="text-amber-600 border-amber-200 hover:bg-amber-50"
          data-testid={`contact-action-analyze-${contact.contact_id}`}
        >
          {loading === 'analyze' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
          <span className="ml-1 hidden sm:inline">Analyser</span>
        </Button>
      </div>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Envoyer un email à {contact.nom_prenom}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Destinataire</label>
              <Input value={contact.email || ''} disabled className="bg-slate-50" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Sujet</label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Sujet de l'email..."
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Message</label>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleGenerateEmail('first_contact')} disabled={generatingEmail}>
                    {generatingEmail ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                    Premier contact
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleGenerateEmail('follow_up')} disabled={generatingEmail}>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Relance
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleGenerateEmail('quote_request')} disabled={generatingEmail}>
                    <FileText className="w-3 h-3 mr-1" />
                    Demande devis
                  </Button>
                </div>
              </div>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Contenu de l'email..."
                rows={8}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowEmailModal(false)}>Annuler</Button>
              <Button onClick={handleSendEmail} disabled={loading === 'send' || !emailSubject || !emailBody}>
                {loading === 'send' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analysis Modal */}
      <Dialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-amber-600" />
              Analyse IA - {contact.nom_prenom}
            </DialogTitle>
          </DialogHeader>
          {analysis ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Score potentiel</p>
                  <p className="text-2xl font-bold text-slate-900">{analysis.score || 0}/100</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Niveau d'intérêt</p>
                  <Badge className={
                    analysis.interest === 'hot' ? 'bg-red-500' :
                    analysis.interest === 'warm' ? 'bg-orange-500' : 'bg-blue-500'
                  }>
                    {analysis.interest === 'hot' ? '🔥 Chaud' : 
                     analysis.interest === 'warm' ? '☀️ Tiède' : '❄️ Froid'}
                  </Badge>
                </div>
              </div>
              {analysis.summary && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 mb-1">Résumé</p>
                  <p className="text-sm text-blue-700">{analysis.summary}</p>
                </div>
              )}
              {analysis.recommended_action && (
                <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
                  <p className="text-sm font-medium text-green-900 mb-1">Action recommandée</p>
                  <p className="text-sm text-green-700">{analysis.recommended_action}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto" />
              <p className="text-slate-500 mt-2">Analyse en cours...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Devis Modal - Simple redirect */}
      <Dialog open={showDevisModal} onOpenChange={setShowDevisModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Créer un devis pour {contact.nom_prenom}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Créer un nouveau devis associé à ce contact ?
            </p>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="font-medium text-purple-900">{contact.nom_prenom}</p>
              <p className="text-sm text-purple-700">{contact.email}</p>
              <p className="text-sm text-purple-700">{contact.telephone}</p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDevisModal(false)}>Annuler</Button>
            <Button 
              onClick={() => {
                window.location.href = `/devis?contact_id=${contact.contact_id}`;
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <FileText className="w-4 h-4 mr-2" />
              Créer le devis
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================
// DEVIS AI ACTIONS
// ============================================

export function DevisAIActions({ devis, contact, onActionComplete }) {
  const [loading, setLoading] = useState(null);
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [relanceEmail, setRelanceEmail] = useState('');

  const handleAnalyzeDevis = async () => {
    setLoading('analyze');
    try {
      const response = await api.post('/ai/analyze-devis', { devis_id: devis.devis_id });
      setAnalysis(response.data);
      setShowAnalysisModal(true);
    } catch (error) {
      toast.error('Erreur d\'analyse');
    } finally {
      setLoading(null);
    }
  };

  const handleGenerateRelance = async () => {
    setLoading('relance');
    try {
      const response = await api.post('/ai/generate-relance', {
        devis_id: devis.devis_id,
        contact_name: contact?.nom_prenom || 'Client',
        montant: devis.montant_total || devis.montant_estime
      });
      setRelanceEmail(response.data.body || '');
      setShowRelanceModal(true);
    } catch (error) {
      toast.error('Erreur de génération');
    } finally {
      setLoading(null);
    }
  };

  const handleSendRelance = async () => {
    if (!contact?.email) {
      toast.error('Pas d\'email pour ce contact');
      return;
    }
    
    setLoading('send');
    try {
      await api.post('/emails/send', {
        to: contact.email,
        subject: `Relance - Devis ${devis.devis_id}`,
        body: relanceEmail,
        contact_id: contact?.contact_id,
        devis_id: devis.devis_id
      });
      
      // Update devis status
      await api.put(`/devis/${devis.devis_id}`, {
        ...devis,
        statut: 'Relancé',
        date_derniere_relance: new Date().toISOString()
      });
      
      toast.success('Relance envoyée !');
      setShowRelanceModal(false);
      onActionComplete?.();
    } catch (error) {
      toast.error('Erreur d\'envoi');
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleGenerateRelance}
          disabled={loading === 'relance' || devis.statut === 'Accepté'}
          className="text-orange-600 border-orange-200 hover:bg-orange-50"
        >
          {loading === 'relance' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          <span className="ml-1">Relancer IA</span>
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={handleAnalyzeDevis}
          disabled={loading === 'analyze'}
          className="text-purple-600 border-purple-200 hover:bg-purple-50"
        >
          {loading === 'analyze' ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
          <span className="ml-1">Probabilité</span>
        </Button>
      </div>

      {/* Relance Modal */}
      <Dialog open={showRelanceModal} onOpenChange={setShowRelanceModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-600" />
              Relancer le devis {devis.devis_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm text-orange-700">
                <strong>Destinataire:</strong> {contact?.email || 'Email non disponible'}
              </p>
              <p className="text-sm text-orange-700">
                <strong>Montant:</strong> {(devis.montant_total || devis.montant_estime || 0).toLocaleString('fr-FR')} €
              </p>
            </div>
            <Textarea
              value={relanceEmail}
              onChange={(e) => setRelanceEmail(e.target.value)}
              rows={10}
              placeholder="Contenu de l'email de relance..."
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRelanceModal(false)}>Annuler</Button>
              <Button 
                onClick={handleSendRelance} 
                disabled={loading === 'send' || !contact?.email}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loading === 'send' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer la relance
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analysis Modal */}
      <Dialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Analyse - Devis {devis.devis_id}
            </DialogTitle>
          </DialogHeader>
          {analysis ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-2">Probabilité de signature</p>
                <div className={`text-5xl font-bold ${
                  analysis.probability >= 70 ? 'text-green-600' :
                  analysis.probability >= 40 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {analysis.probability || 0}%
                </div>
                <Badge className={`mt-2 ${
                  analysis.probability >= 70 ? 'bg-green-500' :
                  analysis.probability >= 40 ? 'bg-amber-500' : 'bg-red-500'
                }`}>
                  {analysis.probability >= 70 ? 'Bonne chance' :
                   analysis.probability >= 40 ? 'Possible' : 'Difficile'}
                </Badge>
              </div>
              {analysis.factors && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Facteurs clés:</p>
                  {analysis.factors.map((factor, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      {factor.positive ? (
                        <ThumbsUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                      )}
                      <span className={factor.positive ? 'text-green-700' : 'text-amber-700'}>
                        {factor.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {analysis.suggestion && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900">Suggestion IA</p>
                  <p className="text-sm text-blue-700">{analysis.suggestion}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================
// CHANTIER AI ACTIONS
// ============================================

export function ChantierAIActions({ chantier, sousTraitants, onActionComplete }) {
  const [loading, setLoading] = useState(null);
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [showAffectationModal, setShowAffectationModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [report, setReport] = useState(null);

  const handleSuggestPlanning = async () => {
    setLoading('planning');
    try {
      const response = await api.post('/ai/suggest-planning', {
        chantier_id: chantier.chantier_id,
        date_debut: chantier.date_debut,
        date_fin: chantier.date_fin_estimee
      });
      setSuggestions(response.data);
      setShowPlanningModal(true);
    } catch (error) {
      toast.error('Erreur de suggestion');
    } finally {
      setLoading(null);
    }
  };

  const handleSuggestAffectation = async () => {
    setLoading('affectation');
    try {
      const response = await api.post('/ai/suggest-affectation', {
        chantier_id: chantier.chantier_id,
        type_travaux: chantier.type_travaux || 'general'
      });
      setSuggestions(response.data);
      setShowAffectationModal(true);
    } catch (error) {
      toast.error('Erreur de suggestion');
    } finally {
      setLoading(null);
    }
  };

  const handleGenerateReport = async () => {
    setLoading('report');
    try {
      const response = await api.post('/ai/generate-report', {
        chantier_id: chantier.chantier_id
      });
      setReport(response.data);
      setShowReportModal(true);
    } catch (error) {
      toast.error('Erreur de génération');
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleSuggestPlanning}
          disabled={loading === 'planning'}
          className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
        >
          {loading === 'planning' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calendar className="w-3 h-3" />}
          <span className="ml-1">Planning IA</span>
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={handleSuggestAffectation}
          disabled={loading === 'affectation'}
          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
        >
          {loading === 'affectation' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" />}
          <span className="ml-1">Affecter IA</span>
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={handleGenerateReport}
          disabled={loading === 'report'}
          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
        >
          {loading === 'report' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
          <span className="ml-1">Rapport IA</span>
        </Button>
      </div>

      {/* Planning Modal */}
      <Dialog open={showPlanningModal} onOpenChange={setShowPlanningModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-600" />
              Planning suggéré - {chantier.nom || chantier.chantier_id}
            </DialogTitle>
          </DialogHeader>
          {suggestions ? (
            <div className="space-y-4">
              {suggestions.phases?.map((phase, idx) => (
                <div key={idx} className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-slate-900">{phase.name}</p>
                    <Badge variant="outline">{phase.duration}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{phase.description}</p>
                </div>
              ))}
              {suggestions.warning && (
                <div className="bg-amber-50 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-700">{suggestions.warning}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600 mx-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Affectation Modal */}
      <Dialog open={showAffectationModal} onOpenChange={setShowAffectationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Sous-traitants suggérés
            </DialogTitle>
          </DialogHeader>
          {suggestions?.recommended?.length > 0 ? (
            <div className="space-y-3">
              {suggestions.recommended.map((st, idx) => (
                <div key={idx} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{st.name}</p>
                    <p className="text-sm text-slate-600">{st.specialite}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-500" />
                      <span className="font-medium">{st.rating}/5</span>
                    </div>
                    <Badge className="bg-green-500 mt-1">Disponible</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Aucune suggestion disponible
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Rapport de chantier
            </DialogTitle>
          </DialogHeader>
          {report ? (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div className="prose prose-sm">
                <pre className="whitespace-pre-wrap bg-slate-50 rounded-lg p-4 text-sm">
                  {report.content}
                </pre>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => {
                  navigator.clipboard.writeText(report.content);
                  toast.success('Copié !');
                }}>
                  Copier
                </Button>
                <Button onClick={() => setShowReportModal(false)}>Fermer</Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================
// SOUS-TRAITANT AI ACTIONS
// ============================================

export function SousTraitantAIActions({ sousTraitant, chantiers, onActionComplete }) {
  const [loading, setLoading] = useState(null);
  const [showDispoModal, setShowDispoModal] = useState(false);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [disponibilite, setDisponibilite] = useState(null);
  const [evaluation, setEvaluation] = useState(null);

  const handleCheckDispo = async () => {
    setLoading('dispo');
    try {
      const response = await api.post('/ai/check-disponibilite', {
        sous_traitant_id: sousTraitant.sous_traitant_id
      });
      setDisponibilite(response.data);
      setShowDispoModal(true);
    } catch (error) {
      toast.error('Erreur de vérification');
    } finally {
      setLoading(null);
    }
  };

  const handleEvaluate = async () => {
    setLoading('eval');
    try {
      const response = await api.post('/ai/evaluate-soustritant', {
        sous_traitant_id: sousTraitant.sous_traitant_id
      });
      setEvaluation(response.data);
      setShowEvalModal(true);
    } catch (error) {
      toast.error('Erreur d\'évaluation');
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleCheckDispo}
          disabled={loading === 'dispo'}
          className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
        >
          {loading === 'dispo' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calendar className="w-3 h-3" />}
          <span className="ml-1">Disponibilité</span>
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          onClick={handleEvaluate}
          disabled={loading === 'eval'}
          className="text-amber-600 border-amber-200 hover:bg-amber-50"
        >
          {loading === 'eval' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
          <span className="ml-1">Évaluer IA</span>
        </Button>
      </div>

      {/* Disponibilité Modal */}
      <Dialog open={showDispoModal} onOpenChange={setShowDispoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-600" />
              Disponibilité - {sousTraitant.nom}
            </DialogTitle>
          </DialogHeader>
          {disponibilite ? (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${disponibilite.available ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-2">
                  {disponibilite.available ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  )}
                  <span className={`font-medium ${disponibilite.available ? 'text-green-700' : 'text-red-700'}`}>
                    {disponibilite.available ? 'Disponible' : 'Occupé'}
                  </span>
                </div>
                {disponibilite.next_available && (
                  <p className="text-sm text-slate-600 mt-2">
                    Prochaine disponibilité: {disponibilite.next_available}
                  </p>
                )}
              </div>
              {disponibilite.current_chantiers?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Chantiers en cours:</p>
                  {disponibilite.current_chantiers.map((ch, idx) => (
                    <div key={idx} className="bg-slate-50 rounded p-2 text-sm mb-1">
                      {ch.nom} - jusqu'au {ch.date_fin}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600 mx-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Evaluation Modal */}
      <Dialog open={showEvalModal} onOpenChange={setShowEvalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-600" />
              Évaluation IA - {sousTraitant.nom}
            </DialogTitle>
          </DialogHeader>
          {evaluation ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-8 h-8 ${i <= evaluation.score ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
                  ))}
                </div>
                <p className="text-2xl font-bold text-slate-900">{evaluation.score}/5</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Chantiers terminés</p>
                  <p className="text-xl font-bold">{evaluation.completed_jobs || 0}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Taux ponctualité</p>
                  <p className="text-xl font-bold">{evaluation.punctuality || 0}%</p>
                </div>
              </div>
              {evaluation.strengths?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Points forts:</p>
                  <div className="flex flex-wrap gap-2">
                    {evaluation.strengths.map((s, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default { ContactAIActions, DevisAIActions, ChantierAIActions, SousTraitantAIActions };
